import { useEffect, useRef } from "react";
import type { CarouselApi } from "@/components/carousel/carousel";

export function useCarouselAutoplay(api: CarouselApi | undefined, delay: number = 3000, pauseOnInteraction: number = 10000) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!api) return;

    const startAutoplay = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        api.scrollNext();
      }, delay);
    };

    const pauseAutoplay = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Clear any existing pause timeout
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
      
      // Resume after pauseOnInteraction milliseconds
      pauseTimeoutRef.current = setTimeout(() => {
        startAutoplay();
      }, pauseOnInteraction);
    };

    // Start autoplay initially
    startAutoplay();

    // Pause on user interaction
    const handlePointerDown = () => pauseAutoplay();
    const handleSelect = () => pauseAutoplay();

    api.on("pointerDown", handlePointerDown);
    api.on("select", handleSelect);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
      api.off("pointerDown", handlePointerDown);
      api.off("select", handleSelect);
    };
  }, [api, delay, pauseOnInteraction]);
}

